import moment from 'moment';
import {
  callApi,
  removeCookies,
  setCookies,
  getNumOfGuestsByType,
  checkAddonsByCode,
  getOnlyAllowStatusMessage,
  getNotAllowedForSunBookersMessage,
  getErrorMessage,
  getNotFoundBookingMessage,
  getNotFullyPaidMessage,
  getHoursAndMinutes,
  checkIfTravelAgentBooking,
  getIfBreakOpenUsingAddon,
  getShowAppModal,
  getSeasonType,
  getSeasonCodeSuffix,
  isQualifyingHbsUsingAddon,
  getExtendedSeasonType,
  getEarliestDateToSignIn,
  getBreakIsFinishedMessage,
  getIsDABooker,
} from '../../utils';
import {
  REQUEST_BOOKING_SEARCH,
  RECEIVE_BOOKING_SEARCH,
  SET_BOOKING_SEARCH_FAILURE,
  CLEAR_BOOKING_SEARCH_ERROR_MESSAGE,
  SET_BOOKING_DATA_FROM_COOKIE,
  SET_BOOKING_SEARCH_LOGIN_DENIED,
  REQUEST_BOOKING_BALANCE,
  RECEIVE_BOOKING_BALANCE,
  SET_BOOKING_BALANCE_FAILURE,
  SET_SHOW_APP_MODAL,
  SET_SITE_CODE,
} from '../constants/bookingSearch';
import { logInOutPushGTM, notFoundBookingGTM } from '../../googleTagManager';

const requestBookingSearch = () => ({
  type: REQUEST_BOOKING_SEARCH,
});

const receiveBookingSearch = params => ({
  type: RECEIVE_BOOKING_SEARCH,
  ...params,
});

const setBookingSearchFailure = error => ({
  type: SET_BOOKING_SEARCH_FAILURE,
  error,
});

export const clearBookingSearchErrorMessage = () => ({
  type: CLEAR_BOOKING_SEARCH_ERROR_MESSAGE,
});

const setBookingSearchLoginDenied = error => ({
  type: SET_BOOKING_SEARCH_LOGIN_DENIED,
  error,
});

const requestGetBookingBalance = () => ({
  type: REQUEST_BOOKING_BALANCE,
});

const receiveBookingBalance = balance => ({
  type: RECEIVE_BOOKING_BALANCE,
  balance,
});

const setBookingBalanceFailure = (error, isLogin) => ({
  type: SET_BOOKING_BALANCE_FAILURE,
  error,
  isLogin,
});

export const setShowAppModal = showAppModal => ({
  type: SET_SHOW_APP_MODAL,
  showAppModal,
});

export const setSiteCode = siteCode => ({
  type: SET_SITE_CODE,
  siteCode,
});

const isArrivalDateBetweenFullyPaidDates = (momentArrivalDate, fullyPaidRequiredStartDate, fullyPaidRequiredEndDate) => {
  const momentFullyPaidStartDate = moment(fullyPaidRequiredStartDate);
  const momentFullyPaidEndDate = moment(fullyPaidRequiredEndDate);
  return momentArrivalDate.isBetween(momentFullyPaidStartDate, momentFullyPaidEndDate, 'day', '[]');
};

export const getBookingBalance = (resId, siteCode, lastName, arrivalDate) => async (dispatch, getState) => {
  dispatch(requestGetBookingBalance());
  const { config } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: bookingSubscriptionKey,
    REACT_APP_API_TIMEOUT_MS: timeout,
  } = config;
  try {
    const formattedArrivalDate = moment(arrivalDate).format('YYYY-MM-DD');
    const { data = {} } = await callApi(
      {
        method: 'get',
        url: `/common/booking/search/balance?resId=${resId}&siteCode=${siteCode.toUpperCase()}&lastName=${lastName.trim()}&arrivalDate=${formattedArrivalDate}`, // eslint-disable-line
      },
      bookingPrefix,
      bookingSubscriptionKey,
      timeout,
    );
    if (data.Data) {
      const { Data: balance = {} } = data;
      dispatch(receiveBookingBalance(balance));
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    dispatch(setBookingBalanceFailure(error));
  }
};

export const searchBooking = ({ resId, siteCode, lastName, arrivalDate, rawBookingRef = '', secretCode = '' }) => async (dispatch, getState) => {
  dispatch(requestBookingSearch());
  const { config } = getState();
  const {
    REACT_APP_BOOKING_PREFIX: bookingPrefix,
    REACT_APP_BOOKING_SUBSCRIPTION_KEY: bookingSubscriptionKey,
    REACT_APP_MAX_ENTERTAINMENT_SLOTS_LESS_THAN_7_NIGHTS,
    REACT_APP_MAX_ENTERTAINMENT_SLOTS_MORE_THAN_OR_EQUAL_7_NIGHTS,
    REACT_APP_FULLY_PAID_REQUIRED_START_DATE: fullyPaidRequiredStartDate,
    REACT_APP_FULLY_PAID_REQUIRED_END_DATE: fullyPaidRequiredEndDate,
    REACT_APP_API_TIMEOUT_MS: timeout,
    REACT_APP_TEAM_ACCESS_OVERRIDE_PASSCODE: teamAccessOverridePasscode,
    REACT_APP_ARRIVAL_TIME: arrivalTime,
    REACT_APP_HBS_ADDON_CODE: hbsAddonCode,
    REACT_APP_DINE_AROUND_ADDON_CODE: dineAroundAddonCode
  } = config;
  try {
    removeCookies();
    const momentArrivalDate = moment(arrivalDate);

    const formattedArrivalDate = momentArrivalDate.format('YYYY-MM-DD');
    const { data = {} } = await callApi(
      {
        method: 'get',
        url: `/common/booking/search?resId=${resId}&siteCode=${siteCode.toUpperCase()}&lastName=${lastName.trim()}&ArrivalDate=${formattedArrivalDate}`, // eslint-disable-line
      },
      bookingPrefix,
      bookingSubscriptionKey,
      timeout,
    );

    if (!data.Data) return;

    const { Units = [] } = data.Data;
    const numOfUnits = Units.filter(({ Booking = {} }) => Booking.status.code === 'BK').length;
    const [firstUnit = {}] = Units;
    const {
      vacationDate = {},
      guests = [],
      addons = [],
      status = {},
      agency = {},
      ResInitDate = '',
      SeasonCode: seasonCode = '',
    } = firstUnit.Booking || {};
    const { type: agencyType = '' } = agency;
    const { code = '' } = status;
    const { to: departureDate = '' } = vacationDate;
    const [mainGuest = {}] = guests;
    const { fullName } = mainGuest;
    const nameChunks = fullName.split(' ');
    const mainGuestFirstName = nameChunks[nameChunks.length - 2];
    const { numOfGuests, numOfAdults, numOfChildren, numOfToddlers, numOfInfants, numOfUnderFifteens } = getNumOfGuestsByType(Units);
    const isTravelAgent = checkIfTravelAgentBooking(agencyType);
    const isSecretCodeApplied = teamAccessOverridePasscode === secretCode;
    const { hours: arrivalHour, minutes: arrivalMinute } = getHoursAndMinutes(arrivalTime);
    const arrivalMoment = moment(arrivalDate).hour(arrivalHour).minute(arrivalMinute).second(0);
    const isOnBreak = moment().isAfter(arrivalMoment);

    const seasonType = getSeasonType(seasonCode);
    const seasonSuffix = getSeasonCodeSuffix(seasonType);
    const isHbs = checkAddonsByCode(addons, hbsAddonCode);
    const isBreakOpen = getIfBreakOpenUsingAddon(config, isHbs, arrivalDate, siteCode, ResInitDate, seasonSuffix);
    const showAppModal = getShowAppModal({ isTravelAgentBooking: isTravelAgent, isSecretCodeApplied, isOnBreak, config, siteCode, arrivalDate });
    const isEarlyAccess = isQualifyingHbsUsingAddon(config, isHbs, arrivalDate, ResInitDate, siteCode, seasonSuffix);

    // Has it DINEAROUND addon?
    const isDaBooking = getIsDABooker(addons, dineAroundAddonCode);
    const isBreakFinished = moment().isAfter(departureDate);

    if (isBreakFinished) {
      dispatch(setBookingSearchLoginDenied(getBreakIsFinishedMessage()));
      return;
    }

    if (showAppModal) {
      setCookies({
        siteCode,
        resId,
        lastName,
        formattedArrivalDate,
        departureDate,
        mainGuestFirstName,
        numOfGuests,
        numOfAdults,
        numOfChildren,
        numOfToddlers,
        numOfInfants,
        numOfUnits,
        numOfUnderFifteens,
        fullName,
        isDaBooking,
        agencyType,
        secretCode,
        isEarlyAccess,
        seasonType,
        isHbs,
      });
      dispatch(setSiteCode(siteCode));
      dispatch(setShowAppModal(true));
      return;
    }

    if (!isBreakOpen) {
      const extendedSeasonType = getExtendedSeasonType(seasonType, isEarlyAccess);
      dispatch(
        setBookingSearchLoginDenied(`Please note you can only book for breaks starting up to 
        ${getEarliestDateToSignIn(config, seasonSuffix, extendedSeasonType).format('Do MMMM YYYY')}, we'll send you an email when you can start booking`),
      );
      return;
    }

    if (isHbs && !isEarlyAccess) {
      dispatch(
        setBookingSearchLoginDenied("Your booking window for your break isn't open just yet. Look out for an email which will tell you when you can start booking"),
      );
      return;
    }

    // checking agency if it has included EXTERNAL_LOAD
    if (agencyType === 'EXTERNAL LOAD') {
      if (!checkAddonsByCode(addons, 'ENTS PASS')) {
        dispatch(
          setBookingSearchLoginDenied(getNotAllowedForSunBookersMessage()),
        );
        return;
      }
    }

    // checking status
    if (code !== 'BK') {
      dispatch(
        setBookingSearchLoginDenied(getOnlyAllowStatusMessage(code)),
      );
      return;
    }

    // checking whether break is fully paid or not, we'll ignore the travel agent bookings
    if ((fullyPaidRequiredStartDate || fullyPaidRequiredEndDate) && !isTravelAgent) {
      try {
        const { data: balanceData = {} } = await callApi(
          {
            method: 'get',
            url: `/common/booking/search/balance?resId=${resId}&siteCode=${siteCode.toUpperCase()}&lastName=${lastName.trim()}&arrivalDate=${formattedArrivalDate}`, // eslint-disable-line
          },
          bookingPrefix,
          bookingSubscriptionKey,
          timeout,
        );
        if (balanceData.Data) {
          const { Data: balance = {} } = balanceData;
          const { Remaining: remaining } = balance;
          const isFullyPaidByPrice = parseFloat(remaining) < 5;
          const isArrivalDateFullyPaidDate = isArrivalDateBetweenFullyPaidDates(
            momentArrivalDate,
            fullyPaidRequiredStartDate,
            fullyPaidRequiredEndDate,
          );
          if (isArrivalDateFullyPaidDate && !isFullyPaidByPrice) {
            dispatch(setBookingSearchLoginDenied(getNotFullyPaidMessage()));
            return;
          }
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
        dispatch(setBookingSearchLoginDenied(getNotFullyPaidMessage()));
        return;
      }
    }

    setCookies({
      siteCode,
      resId,
      lastName,
      formattedArrivalDate,
      departureDate,
      mainGuestFirstName,
      numOfGuests,
      numOfAdults,
      numOfChildren,
      numOfToddlers,
      numOfInfants,
      numOfUnits,
      numOfUnderFifteens,
      fullName,
      isDaBooking,
      agencyType,
      secretCode,
      isEarlyAccess,
      seasonType,
      isHbs,
    });

    logInOutPushGTM('login', 'success');

    dispatch(receiveBookingSearch({
      booking:
        data.Data,
      resId: parseInt(resId, 10),
      siteCode,
      lastName,
      arrivalDate,
      departureDate,
      mainGuestFirstName,
      numOfGuests,
      numOfAdults,
      numOfChildren,
      numOfToddlers,
      numOfInfants,
      numOfUnits,
      fullName,
      agencyType,
      maxEntertainmentSlotsLessThan7Nights: parseInt(REACT_APP_MAX_ENTERTAINMENT_SLOTS_LESS_THAN_7_NIGHTS, 10) || 1,
      maxEntertainmentSlotsMoreThanOrEqual7Nights: parseInt(REACT_APP_MAX_ENTERTAINMENT_SLOTS_MORE_THAN_OR_EQUAL_7_NIGHTS, 10) || 2,
      isDaBooking,
      secretCode,
      isEarlyAccess,
      seasonType,
      isHbs,
    }));
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error(error); // eslint-disable-line
    if (getErrorMessage(error, true) === getNotFoundBookingMessage()) {
      notFoundBookingGTM(rawBookingRef, arrivalDate);
    }
    dispatch(setBookingSearchFailure(error));
  }
};

export const setSearchBookingDataFromCookie = params => ({
  type: SET_BOOKING_DATA_FROM_COOKIE,
  ...params,
});
